<template>
  <div
    class="absolute left-0 top-0 z-40"
    :class="{ 'h-full w-full': current === 'menu' }"
    @click="showRail"
  >
    <nav
      :key="current"
      class="sidebar-pane overflow-y-auto"
      @click.stop
      @animationend="onSlideEnd"
      :class="[
        current === 'menu'
          ? 'sidebar-menu sidebar-expanded divide-y-1 z-20 flex h-full w-[288px] shrink-0 flex-col justify-between divide-gray-secondary border-r border-gray-secondary bg-black-opaced p-5 backdrop-blur-2xl'
          : 'sidebar-rail relative z-10 w-[52px] pl-4',
        phase === 'out' ? 'sidebar-slide-out' : '',
        phase === 'in' ? 'sidebar-slide-in' : '',
      ]"
    >
      <HamburgerIcon
        v-if="current === 'rail'"
        @click.stop="showMenu"
        class="absolute left-4 top-4 z-10 h-9 w-9 cursor-pointer rounded-[5px] border border-gray-secondary bg-black-primary bg-opacity-20 p-2 text-white-primary backdrop-blur-xl hover:bg-opacity-30"
      />
      <div>
        <div
          v-if="current === 'menu'"
          class="sidebar-brand flex items-center justify-between"
        >
          <RouterLink to="/dashboard" @click="showRail">
            <ZTSIcon class="sidebar-logo" />
          </RouterLink>
        </div>

        <div
          class="rounded-[5px] text-white-primary"
          :class="
            current === 'menu'
              ? 'w-full flex-1'
              : 'mt-20 w-9 border border-gray-secondary bg-black-opaced py-1 backdrop-blur-[200px]'
          "
        >
          <div
            v-for="(group, i) in tabGroups"
            class="flex flex-col py-2"
            :class="{ 'items-center': current === 'rail' }"
          >
            <p
              v-if="current === 'menu'"
              class="sidebar-label sidebar-label-group text-text-secondary text-gray-primary"
            >
              {{ group.name }}
            </p>
            <div
              class="flex flex-col gap-3"
              :class="{ 'items-center': current === 'rail' }"
            >
              <div v-for="(tab, idx) in group.tabs" :key="idx">
                <RouterLink
                  :to="tab.uri[0]"
                  class="flex items-center"
                  data-testid="sidebar-tab"
                  :data-active="tab.uri.includes(activeTab) ? 'true' : 'false'"
                  @click="showRail"
                  :class="[
                    current === 'menu' ? 'gap-3' : 'gap-0',
                    tab.uri.includes(activeTab)
                      ? 'text-green-primary'
                      : 'text-white-primary',
                  ]"
                >
                  <div :title="tab.name">
                    <component :is="tab.icon" class="h-5 w-5 shrink-0" />
                  </div>
                  <span
                    v-if="current === 'menu'"
                    class="sidebar-label text-[14px] leading-[116%]"
                  >
                    {{ tab.name }}
                  </span>
                </RouterLink>
              </div>
              <div
                v-if="i != tabGroups.length - 1 && current === 'rail'"
                class="z-10 w-4 border-t-2 border-dashed border-gray-secondary"
              ></div>
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="current === 'menu'"
        class="sidebar-footer flex w-full flex-col text-white-primary"
        data-testid="sidebar-footer"
      >
        <div class="flex w-full justify-between">
          <RouterLink
            to="/user-settings/profile"
            @click="showRail"
            class="flex w-[80%] items-center gap-3"
          >
            <div class="flex h-8 w-8 items-center justify-center rounded-full">
              <UserSquare class="" />
            </div>
            <div class="w-[80%]">
              <p class="truncate text-text-primary text-white-primary">
                {{ getUserLogin() }}
              </p>
              <p class="truncate text-text-secondary text-gray-primary">
                {{ tokenInfo?.roles[0] }}
              </p>
            </div>
          </RouterLink>
          <div
            class="bg-opaced-white-primary flex h-6 w-6 cursor-pointer items-center justify-center self-end rounded-md border border-gray-secondary text-text-secondary"
            data-testid="sidebar-logout"
            :title="t('auth.logout')"
            @click="logout"
          >
            <GetOutIcon class="h-3 w-3" />
          </div>
        </div>
        <p
          data-testid="sidebar-app-version"
          class="sidebar-version mt-2 truncate text-text-secondary text-gray-primary"
        >
          {{ t("app.version", { version: APP_VERSION }) }}
        </p>
      </div>
    </nav>
  </div>
</template>

<script setup lang="ts">
import { computed, onUnmounted, ref } from "vue";
import router from "@/router";
import MonitoringIcon from "@/icons/monitoring.svg";
import HamburgerIcon from "@/icons/hamburger.svg";
import OverviewIcon from "@/icons/overview.svg";
import TemplateIcon from "@/icons/template.svg";
import AlertIcon from "@/icons/alert.svg";
import IntegrationIcon from "@/icons/integration.svg";
import MailIcon from "@/icons/mail.svg";
import WorldIcon from "@/icons/world.svg";
import UserSquare from "@/icons/user-square.svg";
import RoleIcon from "@/icons/role.svg";
import SettingsIcon from "@/icons/settings.svg";
import EventsIcon from "@/icons/events.svg";
import CircleIcon from "@/icons/circle.svg";
import ZTSIcon from "@/icons/zts-logo.svg";
import GetOutIcon from "@/icons/get-out.svg";
import NetShieldIcon from "@/icons/net-shield.svg";
import LockIcon from "@/icons/lock.svg";
import { getUserLogin } from "@/helpers";
import { useI18n } from "vue-i18n";
import { Locales, MessageSchema } from "@/i18n";
import { UserRole } from "@/types";
import { useAuthStore } from "@/stores/auth/session";
import { storeToRefs } from "pinia";
import { APP_VERSION } from "@/version";
import { prefersReducedMotion } from "@/utils/appleMotion";

/** Matches `--duration-sidebar` in style.css. */
const SIDEBAR_SLIDE_MS = 260;

const { t } = useI18n<{ message: MessageSchema }, Locales>();

const activeTab = computed(() => router.currentRoute.value.path);

type SidebarPane = "rail" | "menu";
const current = ref<SidebarPane>("rail");
const phase = ref<"idle" | "out" | "in">("idle");
let nextPane: SidebarPane = "rail";
let slideTimer = 0;
let slideToken = 0;

function startSlide(next: SidebarPane) {
  if (current.value === next || phase.value !== "idle") return;
  if (prefersReducedMotion()) {
    current.value = next;
    return;
  }
  nextPane = next;
  phase.value = "out";
  armSlideTimer();
}

function clearSlideTimer() {
  clearTimeout(slideTimer);
  slideTimer = 0;
}

function armSlideTimer() {
  const token = ++slideToken;
  clearSlideTimer();
  slideTimer = window.setTimeout(() => {
    if (token !== slideToken) return;
    advanceSlide();
  }, SIDEBAR_SLIDE_MS + 50);
}

function advanceSlide() {
  clearSlideTimer();
  slideToken += 1;
  if (phase.value === "out") {
    current.value = nextPane;
    phase.value = "in";
    armSlideTimer();
    return;
  }
  if (phase.value === "in") phase.value = "idle";
}

function onSlideEnd(event: AnimationEvent) {
  if (event.target !== event.currentTarget || phase.value === "idle") return;
  if (phase.value === "out" && event.animationName !== "sidebar-slide-out") return;
  if (phase.value === "in" && event.animationName !== "sidebar-slide-in") return;
  advanceSlide();
}

function showMenu() {
  startSlide("menu");
}

function showRail() {
  startSlide("rail");
}

onUnmounted(() => {
  slideToken += 1;
  clearSlideTimer();
});

const tabGroups = computed(() => {
  const viewTabs = tokenInfo.value?.roles.some((r) =>
    [UserRole.Viewer, UserRole.SegViewer].includes(r),
  )
    ? [
        {
          uri: ["/dashboard"],
          icon: MonitoringIcon,
          name: t("nav.dashboard"),
        },
        {
          uri: ["/network"],
          icon: OverviewIcon,
          name: t("nav.network"),
        },
        {
          uri: [
            "/hosts/network",
            "/hosts/external",
            "/hosts/groups",
            "/hosts/tags",
          ],
          icon: CircleIcon,
          name: t("nav.hosts"),
        },
        {
          uri: ["/rules/current", "/rules/log", "/rules/recommendations"],
          icon: NetShieldIcon,
          name: t("nav.rules"),
        },
      ]
    : [
        {
          uri: ["/dashboard"],
          icon: MonitoringIcon,
          name: t("nav.dashboard"),
        },
        {
          uri: ["/network"],
          icon: OverviewIcon,
          name: t("nav.network"),
        },
        {
          uri: [
            "/hosts/network",
            "/hosts/external",
            "/hosts/groups",
            "/hosts/tags",
          ],
          icon: CircleIcon,
          name: t("nav.hosts"),
        },
        {
          uri: ["/rules/current", "/rules/log", "/rules/recommendations"],
          icon: NetShieldIcon,
          name: t("nav.rules"),
        },
        {
          uri: ["/templates"],
          icon: TemplateIcon,
          name: t("nav.templates"),
        },
      ];

  const eventsTabs =
    !tokenInfo.value?.roles.includes(UserRole.SegAdmin) &&
    !tokenInfo.value?.roles.includes(UserRole.SegViewer)
      ? [
          {
            uri: ["/events/netflow"],
            icon: WorldIcon,
            name: t("nav.netflow"),
          },
          {
            uri: ["/events/process"],
            icon: EventsIcon,
            name: t("nav.process"),
          },
          {
            uri: ["/events/alerts"],
            icon: AlertIcon,
            name: t("nav.alerts"),
          },
          {
            uri: ["/events/firewall"],
            icon: LockIcon,
            name: t("nav.firewall"),
          },
        ]
      : [
          {
            uri: ["/events/process"],
            icon: EventsIcon,
            name: t("nav.process"),
          },
          {
            uri: ["/events/alerts"],
            icon: AlertIcon,
            name: t("nav.alerts"),
          },
          {
            uri: ["/events/firewall"],
            icon: LockIcon,
            name: t("nav.firewall"),
          },
        ];

  const roles = tokenInfo.value?.roles ?? [];
  const isViewer = roles.includes(UserRole.Viewer);
  const isAdmin = roles.includes(UserRole.Admin);
  const isSegAdmin = roles.includes(UserRole.SegAdmin);
  const isSegViewer = roles.includes(UserRole.SegViewer);

  const generalUris = ["/general-settings/general"];
  if (isAdmin) generalUris.push("/general-settings/license");

  const userUris = isAdmin
    ? [
        "/user-settings/users",
        "/user-settings/segments",
        "/user-settings/profile",
      ]
    : [
        "/user-settings/profile",
        "/user-settings/users",
        "/user-settings/segments",
      ];

  const settingsTabs = [
    {
      uri: generalUris,
      icon: SettingsIcon,
      name: t("nav.general_settings"),
    },
    {
      uri: userUris,
      icon: RoleIcon,
      name: t("nav.user_settings"),
    },
    ...(isViewer || isSegViewer
      ? []
      : [
          {
            uri: ["/notifications/channels", "/notifications/rules"],
            icon: MailIcon,
            name: t("nav.notification_settings"),
          },
        ]),
    ...(isViewer || isSegAdmin || isSegViewer
      ? []
      : [
          {
            uri: ["/settings-auth"],
            icon: IntegrationIcon,
            name: t("nav.auth_settings"),
          },
        ]),
  ];

  return [
    {
      name: t("nav.network_header"),
      tabs: viewTabs,
    },
    {
      name: t("nav.events_header"),
      tabs: eventsTabs,
    },
    {
      name: t("nav.settings_header"),
      tabs: settingsTabs,
    },
  ];
});

const authStore = useAuthStore();
const { token: tokenInfo } = storeToRefs(authStore);

function logout() {
  authStore.logout({ redirectToLogin: true });
}
</script>
